import { useState, useEffect, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Menu, ShoppingCart, Trash2, Plus, Minus } from "lucide-react";
import { SiX, SiFacebook, SiLinkedin, SiInstagram } from "react-icons/si";
import { NAV_LINKS } from "@/lib/index";
import type { CartItem } from "@/lib/index";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";

// ─── Cart Context ──────────────────────────────────────────────────────────────
interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (id: string) => void;
  updateQty: (id: string, delta: number) => void;
  clearCart: () => void;
  total: number;
}

export const CartContext = createContext<CartContextType | null>(null);

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  // Cart state
  const [items, setItems] = useState<CartItem[]>([]);

  // Navega a sección: scroll directo si estamos en home, si no va a home y hace scroll
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    e.preventDefault();
    if (isHome) {
      const el = document.querySelector(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.hash = "/";
      setTimeout(() => {
        const el = document.querySelector(anchor);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 350);
    }
  };

  const addItem = (item: Omit<CartItem, "quantity">) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    setCartOpen(true);
  };

  const removeItem = (id: string) => setItems((prev) => prev.filter((i) => i.id !== id));

  const updateQty = (id: string, delta: number) => {
    setItems((prev) =>
      prev
        .map((i) => i.id === id ? { ...i, quantity: i.quantity + delta } : i)
        .filter((i) => i.quantity > 0)
    );
  };

  const clearCart = () => setItems([]);
  const total = items.reduce((sum, i) => sum + i.priceNumber * i.quantity, 0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, total }}>
    <div className="min-h-screen flex flex-col">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background/80 backdrop-blur-lg border-b border-primary/20 shadow-[0_8px_30px_-6px_color-mix(in_srgb,var(--primary)_15%,transparent)]"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <a
              href={isHome ? "#hero" : "#/"}
              className="text-2xl font-bold bg-gradient-to-r from-primary to-chart-1 bg-clip-text text-transparent"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              SofIA
            </a>

            <nav className="hidden md:flex items-center gap-8">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.anchor}
                  href={link.anchor}
                  onClick={(e) => handleNavClick(e, link.anchor)}
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors relative group cursor-pointer"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </a>
              ))}
            </nav>

            {/* Cart button (desktop) */}
            <button
              onClick={() => setCartOpen(true)}
              className="hidden md:flex relative items-center justify-center w-10 h-10 rounded-full bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-all"
              aria-label="Carrito de compras"
            >
              <ShoppingCart className="w-5 h-5 text-primary" />
              {items.length > 0 && (
                <Badge className="absolute -top-2 -right-2 w-5 h-5 p-0 flex items-center justify-center bg-primary text-primary-foreground text-xs shadow-[0_0_10px_rgba(168,85,247,0.7)]">
                  {items.reduce((s, i) => s + i.quantity, 0)}
                </Badge>
              )}
            </button>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-card/95 backdrop-blur-lg border-primary/20">
                <div className="flex flex-col gap-6 mt-8">
                  {NAV_LINKS.map((link) => (
                    <a
                      key={link.anchor}
                      href={link.anchor}
                      onClick={(e) => { setMobileMenuOpen(false); handleNavClick(e, link.anchor); }}
                      className="text-lg font-medium text-foreground hover:text-primary transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ))}
                  {/* Cart button mobile */}
                  <button
                    onClick={() => { setMobileMenuOpen(false); setCartOpen(true); }}
                    className="flex items-center gap-2 text-lg font-medium text-primary"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    Carrito
                    {items.length > 0 && (
                      <Badge className="bg-primary text-primary-foreground">
                        {items.reduce((s, i) => s + i.quantity, 0)}
                      </Badge>
                    )}
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* ─── Cart Drawer ──────────────────────────────────── */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="right" className="w-[380px] bg-card/95 backdrop-blur-lg border-primary/20 flex flex-col">
          <SheetHeader className="border-b border-primary/20 pb-4">
            <SheetTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: "Orbitron, sans-serif" }}>
              <ShoppingCart className="w-5 h-5 text-primary" />
              Carrito de Compras
            </SheetTitle>
          </SheetHeader>

          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 text-muted-foreground">
              <ShoppingCart className="w-16 h-16 opacity-20" />
              <p className="text-center">Tu carrito está vacío.<br />Agrega un plan para comenzar.</p>
              <Button variant="outline" className="border-primary/40 hover:bg-primary/10" onClick={() => setCartOpen(false)}>
                Ver Planes
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto py-4 space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="bg-background/40 border border-primary/20 rounded-xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-foreground">{item.name}</p>
                        <p className="text-sm text-muted-foreground">{item.price}{item.period}</p>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQty(item.id, -1)}
                        className="w-7 h-7 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all"
                      >
                        <Minus className="w-3 h-3 text-primary" />
                      </button>
                      <span className="w-6 text-center font-mono font-semibold">{item.quantity}</span>
                      <button
                        onClick={() => updateQty(item.id, 1)}
                        className="w-7 h-7 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all"
                      >
                        <Plus className="w-3 h-3 text-primary" />
                      </button>
                      <span className="ml-auto text-sm font-semibold text-primary" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        ${(item.priceNumber * item.quantity).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-primary/20 pt-4 space-y-4">
                <Separator className="bg-primary/10" />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Subtotal mensual</span>
                  <span className="text-2xl font-bold text-primary" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    ${total.toLocaleString()}/mes
                  </span>
                </div>
                <Button
                  className="w-full shadow-[0_0_20px_rgba(168,85,247,0.5)] hover:shadow-[0_0_30px_rgba(168,85,247,0.7)]"
                  size="lg"
                >
                  Proceder al Pago
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground hover:text-destructive"
                  onClick={clearCart}
                >
                  Vaciar carrito
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <main className="flex-1">{children}</main>

      <footer className="bg-card/50 border-t border-primary/20 mt-24">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h3
                className="text-2xl font-bold bg-gradient-to-r from-primary to-chart-1 bg-clip-text text-transparent mb-4"
                style={{ fontFamily: "Orbitron, sans-serif" }}
              >
                SofIA
              </h3>
              <p className="text-muted-foreground text-sm">
                Agentes de inteligencia artificial para transformar tu vida diaria.
                Tecnología avanzada al servicio de tu productividad.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-4">Enlaces Rápidos</h4>
              <ul className="space-y-2">
                {NAV_LINKS.slice(1).map((link) => (
                  <li key={link.anchor}>
                    <a
                      href={link.anchor}
                      onClick={(e) => handleNavClick(e, link.anchor)}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-4">Síguenos</h4>
              <div className="flex gap-4">
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 hover:scale-110"
                  aria-label="Twitter"
                >
                  <SiX className="w-5 h-5" />
                </a>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 hover:scale-110"
                  aria-label="Facebook"
                >
                  <SiFacebook className="w-5 h-5" />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 hover:scale-110"
                  aria-label="LinkedIn"
                >
                  <SiLinkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 hover:scale-110"
                  aria-label="Instagram"
                >
                  <SiInstagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-primary/20 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 SofIA. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-6">
              <a href="#/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Términos y Condiciones
              </a>
              <a href="#/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Política de Privacidad
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </CartContext.Provider>
  );
}